import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

const baseUrlRfq = process.env.REACT_APP_RFQ_MANAGEMENT;
const baseUrlTxn = process.env.REACT_APP_TRANSACTIONS_MANAGEMENT;

const getErrorMessage = (error) => error.response?.data?.serviceMessage || error.message;

const normalizeBuyers = (payload) => ({
    buyers: (Array.isArray(payload) ? payload : []).map((b) => ({
        ulid: b.buyer_organization_ulid,
        name: b.buyer_organization_name,
    })),
});

const normalizeSummary = (payload) => ({
    trade_support_utilized: payload.utilized_TSL ?? 0,
    trade_support_balance: payload.balance_TSL ?? 0,
    trade_support_allocated: payload.allocated_trade_support_limit ?? 0,
    portfolio: {
        active: { amount: payload.active_transactions_amount ?? 0 },
        pipeline: { amount: payload.pipeline_transactions_amount ?? 0 },
        closed: { amount: payload.closed_transactions_amount ?? 0 },
    },
});

const formatVesselNameImo = (name, imo) => {
    if (name && imo) return `${name} | ${imo}`;
    return name || imo || null;
};

const getTransactionRows = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.transactions)) {
        return payload.transactions;
    }
    return [];
};

const DEFAULT_TRANSACTION_PAGE_SIZE = 20;

const hasNextPageCursor = (cursor) => cursor != null && cursor !== '' && cursor !== 0 && cursor !== '0';

const readPaginationField = (source, envelope, key) => source?.[key] ?? envelope?.[key];

const buildTransactionListParams = ({ buyerOrgId, category, cursor, limit }) => {
    const pageLimit = Number(limit);
    const params = {
        limit: Number.isFinite(pageLimit) && pageLimit > 0 ? pageLimit : DEFAULT_TRANSACTION_PAGE_SIZE,
    };
    if (buyerOrgId) {
        params.buyer_ulid = buyerOrgId;
    }
    if (category && category !== 'all') {
        params.flag = category;
    }
    if (hasNextPageCursor(cursor)) {
        params.cursor = String(cursor);
    }
    return params;
};

const normalizeTransactions = (payload, envelope = {}) => {
    const rows = getTransactionRows(payload);
    const source = payload && !Array.isArray(payload) ? payload : envelope;
    const nextCursor = readPaginationField(source, envelope, 'next_cursor') ?? null;
    const hasNext = Boolean(readPaginationField(source, envelope, 'has_next')) || hasNextPageCursor(nextCursor);

    return {
        transactions: rows.map((row) => ({
            ...row,
            vessel_name_imo: formatVesselNameImo(row.vessel_name, row.vessel_imo),
        })),
        next_cursor: nextCursor,
        has_next: hasNext,
        totalCount: readPaginationField(source, envelope, 'total_count') ?? readPaginationField(source, envelope, 'totalCount'),
    };
};

const normalizeSupplierCreditSummary = (payload) => ({
    trade_support_utilized: payload.utilized_amount ?? 0,
    trade_support_balance: payload.balance_amount ?? 0,
    trade_support_allocated: payload.allocated_amount ?? 0,
    portfolio: {
        active: { amount: payload.active_transactions_amount ?? 0 },
        pipeline: { amount: payload.pipeline_transactions_amount ?? 0 },
        closed: { amount: payload.closed_transactions_amount ?? 0 },
    },
});

export const getTradeSupportBuyers = createAsyncThunk(
    'tradeSupport/getTradeSupportBuyers',
    async (supplierOrgId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${baseUrlRfq}/getAllBuyersForDealFinancing/${supplierOrgId}`);
            const payload = response.data?.payload ?? response.data;
            return normalizeBuyers(payload);
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(message);
            return rejectWithValue(message);
        }
    },
);

export const getTradeSupportSummary = createAsyncThunk(
    'tradeSupport/getTradeSupportSummary',
    async ({ supplierOrgId, buyerOrgId }, { rejectWithValue }) => {
        try {
            const params = buyerOrgId ? { buyer_ulid: buyerOrgId } : {};
            const response = await axios.get(
                `${baseUrlTxn}/tradeSupportLimitUtilization/supplier/${supplierOrgId}/summary`,
                { params },
            );
            const payload = response.data?.payload ?? response.data;
            return normalizeSummary(payload);
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(message);
            return rejectWithValue(message);
        }
    },
);

export const getTradeSupportTransactions = createAsyncThunk(
    'tradeSupport/getTradeSupportTransactions',
    async ({ supplierOrgId, buyerOrgId, category, cursor, limit = DEFAULT_TRANSACTION_PAGE_SIZE }, { rejectWithValue }) => {
        try {
            const params = buildTransactionListParams({ buyerOrgId, category, cursor, limit });
            const response = await axios.get(
                `${baseUrlTxn}/tradeSupportLimitUtilization/supplier/${supplierOrgId}/transactions`,
                { params },
            );
            const payload = response.data?.payload ?? response.data;
            return normalizeTransactions(payload, response.data);
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(message);
            return rejectWithValue(message);
        }
    },
);

export const getSupplierCreditFacilitySummary = createAsyncThunk(
    'tradeSupport/getSupplierCreditFacilitySummary',
    async ({ supplierOrgId, buyerOrgId }, { rejectWithValue }) => {
        try {
            const params = buyerOrgId ? { buyer_ulid: buyerOrgId } : {};
            const response = await axios.get(
                `${baseUrlTxn}/supplierCreditFacility/supplier/${supplierOrgId}/summary`,
                { params },
            );
            const payload = response.data?.payload ?? response.data;
            return normalizeSupplierCreditSummary(payload);
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(message);
            return rejectWithValue(message);
        }
    },
);

export const getSupplierCreditFacilityTransactions = createAsyncThunk(
    'tradeSupport/getSupplierCreditFacilityTransactions',
    async ({ supplierOrgId, buyerOrgId, category, cursor, limit = DEFAULT_TRANSACTION_PAGE_SIZE }, { rejectWithValue }) => {
        try {
            const params = buildTransactionListParams({ buyerOrgId, category, cursor, limit });
            const response = await axios.get(
                `${baseUrlTxn}/supplierCreditFacility/supplier/${supplierOrgId}/transactions`,
                { params },
            );
            const payload = response.data?.payload ?? response.data;
            return normalizeTransactions(payload, response.data);
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(message);
            return rejectWithValue(message);
        }
    },
);
